import axios, { AxiosInstance } from 'axios';
import moment from 'moment';
import { FileBase64, FileLink, Invoice } from '../model/invoices/invoices';
import { Auth } from './AuthService';

const querystring = require('querystring');

export class InvoiceService {
  private static instance: InvoiceService | undefined;

  private logger = console;

  private auth;

  private invoiceUrl;

  private axiosInstance: AxiosInstance = axios.create({
    timeout: 30000,
    transitional: {
      silentJSONParsing: false,
      forcedJSONParsing: true,
      clarifyTimeoutError: true,
    },
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
    },
  });

  public static getInstance(logger, auth, invoiceUrl): InvoiceService {
    if (!this.instance) {
      this.instance = new InvoiceService();
      this.instance.init(logger, auth, invoiceUrl);
    }

    return this.instance;
  }

  private init(logger, auth, invoiceUrl): void {
    const self = this;

    self.logger = logger;
    self.auth = auth;
    self.invoiceUrl = invoiceUrl;

    // Add a request interceptor
    this.axiosInstance.interceptors.request.use(
      async function (config) {
        const accessToken = await self.getAccessToken();
        // Add auth. headers
        config.headers['Authorization'] = `Bearer ${accessToken}`;
        self.logger.debug(
          `Axios request ${config.method} ${config.url}`,
          config
        );
        return config;
      },
      function (error) {
        // Do something with request error
        self.logger.warn('Axios interceptor request', error);
        return Promise.reject(error);
      }
    );
  }

  public async getAccessToken(): Promise<string | undefined> {
    const wpToken = await Auth.getWpToken();
    return wpToken ? wpToken : this.auth.session.token?.accessToken;
  }

  private fetch(resource: string, params?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstance.get(resource + stringParams);
  }

  public getInvoices(): Promise<Invoice[]> {
    return new Promise((resolve, reject) => {
      this.fetch(this.invoiceUrl).then((res) => {
        this.logger.info('Resolving list of invoices');
        resolve(res.data);
      });
    });
  }

  public async getBase64File(link: FileLink): Promise<FileBase64> {
    if (link.type === 'url') {
      return this.downloadInvoice(link.url);
    } else if (link.type === 'base64') {
      const capture = /^data:([a-z]+\/[a-z]+);base64,([-A-Za-z0-9+/]*={0,3})=$/
        .exec(link.url)
        ?.slice(1);
      if (capture === undefined) {
        throw new Error('Invalid Data Url');
      }
      const filename = '';
      const [mime, base64] = capture;
      return { filename, mime, base64 };
    } else {
      throw new Error('Invalid Link type');
    }
  }

  private downloadInvoice(invoice_download_url: string): Promise<FileBase64> {
    return new Promise((resolve, reject) => {
      this.fetch(invoice_download_url).then((res) => {
        resolve(res.data as FileBase64);
      });
    });
  }

  public formatDate(date: string): string {
    const dateRebase = parseInt(date.split('-')[1]) - 1;
    return (
      moment().month(dateRebase).format('MMMM').charAt(0).toUpperCase() +
      moment().month(dateRebase).format('MMMM').slice(1)
    );
  }
}
